import { isPaidBundle } from "@/helpers/bundle";
import { BUNDLE_CART_ACTIONS, BUNDLE_PAYMENT_TYPES } from "@/helpers/payment/constants";
import { isDummyPlan, isFreemiumPlan } from "@/helpers/plansHelpers";
import { isMailSuiteProduct } from "@/helpers/product";
import { toFiniteNumber } from "@/helpers/mathUtils";

/**
 * Validates required fields for a function
 * @param {Object} fields - Object with field names as keys and values to validate
 * @param {string} functionName - Name of the function for error messages
 * @throws {Error} If any required field is missing or empty
 */
function validateRequiredFields(fields, functionName) {
  const vowels = ["a", "e", "i", "o", "u"];
  for (const [key, value] of Object.entries(fields)) {
    if (value == null || value === "") {
      const article = vowels.includes(key.charAt(0).toLowerCase()) ? "an" : "a";
      throw new Error(`\`${functionName}\` requires ${article} ${key}`);
    }
  }
}

/**
 * Adds account count to properties if valid for MailSuite products
 * @param {Object} properties - Properties object to modify
 * @param {string} orderType - The order type to check
 * @param {number|null} accountCount - The account count to validate and add
 */
function addAccountCountIfValid(properties, orderType, accountCount) {
  if (isMailSuiteProduct(orderType) && accountCount != null) {
    const accounts = toFiniteNumber(accountCount, null);
    if (accounts != null) {
      properties.noOfAccounts = accounts;
    }
  }
}

/**
 * Creates a cart action with the specified type and properties
 * @param {string} actionType - The type of action to create
 * @param {Object} properties - Properties for the action
 * @returns {Object} The cart action object
 * @throws {Error} If actionType is missing
 */
export function createCartAction(actionType, properties = {}) {
  if (!actionType) {
    throw new Error("`createCartAction` requires an action type");
  }

  return {
    actionType,
    properties,
  };
}

/**
 * Creates a change mail order number of accounts action
 * @param {Object} params - Action parameters
 * @param {number} params.orderId - The order ID
 * @param {number} params.additionalAccountsToBuy - Number of additional accounts to buy
 * @returns {Object|null} The cart action or null if invalid
 */
export function createChangeMailOrderNoOfAccountsAction({ orderId, additionalAccountsToBuy }) {
  const accountsToBuy = toFiniteNumber(additionalAccountsToBuy, null);

  if (!orderId || accountsToBuy == null || accountsToBuy <= 0) {
    return null;
  }

  return createCartAction(BUNDLE_CART_ACTIONS.CHANGE_MAIL_ORDER_NO_OF_ACCOUNTS, {
    orderId,
    additionalAccountsToBuy: accountsToBuy,
  });
}

/**
 * Attaches bundle metadata to a payload object
 * @param {Object} payload - The payload object to attach metadata to
 * @param {Object} bundle - Bundle metadata object
 * @returns {Object} The payload with attached metadata
 */
function attachBundleMetadata(payload, bundle = {}) {
  const metadata = {
    ...(bundle.bundleId && { bundleId: bundle.bundleId }),
    ...(bundle.currency && { currency: bundle.currency }),
    ...(bundle.promoCodeId != null && { promoCodeId: bundle.promoCodeId }),
    ...(bundle.clientMetadata && { clientMetadata: bundle.clientMetadata }),
    paymentType: bundle.paymentType || BUNDLE_PAYMENT_TYPES.CONTROL_PANEL_PURCHASE,
  };

  return Object.assign(payload, metadata);
}

/**
 * Creates a bundle purchase payload with actions and bundle metadata
 * @param {Object} params - Payload parameters
 * @param {Array} params.actions - Array of cart actions
 * @param {Object} [params.bundle={}] - Bundle metadata
 * @returns {Object} The complete bundle purchase payload
 * @throws {Error} If actions array is empty or invalid
 */
export function createBundlePurchasePayload({ actions = [], bundle = {} } = {}) {
  if (!Array.isArray(actions) || !actions.length) {
    throw new Error("`createBundlePurchasePayload` requires at least one action");
  }

  const payload = {
    cart: {
      actions,
    },
  };
  return attachBundleMetadata(payload, bundle);
}

/**
 * Creates a change order plan action
 * @param {Object} params - Action parameters
 * @param {number} params.orderId - The order ID
 * @param {string} params.orderType - The order type (e.g., "MailSuite", "Site")
 * @param {string} params.planType - The plan type (e.g., "premium", "pro")
 * @param {number} [params.additionalAccountsToBuy] - Additional accounts to buy (for MailSuite)
 * @param {Object} [params.properties={}] - Additional properties
 * @returns {Object} The cart action
 * @throws {Error} If required parameters are missing
 */
export function createChangeOrderPlanAction({
  orderId,
  orderType,
  planType,
  additionalAccountsToBuy,
  properties = {},
} = {}) {
  validateRequiredFields({ orderType, planType, orderId }, "createChangeOrderPlanAction");

  const actionProperties = {
    orderType,
    planType,
    orderId,
    ...properties,
  };

  const accountsToBuy = toFiniteNumber(additionalAccountsToBuy);
  if (isMailSuiteProduct(orderType) && accountsToBuy != null && accountsToBuy > 0) {
    actionProperties.additionalAccountsToBuy = accountsToBuy;
  }

  return createCartAction(BUNDLE_CART_ACTIONS.CHANGE_ORDER_PLAN, actionProperties);
}

/**
 * Creates a change order plan action
 * @param {Object} params - Action parameters
 * @param {Object} [params.properties={}] - Additional properties
 * @returns {Object} The cart action
 * @throws {Error} If required parameters are missing
 */
export function createUpdateBundleFreeToPaidAction({ properties = {} } = {}) {
  return createCartAction(BUNDLE_CART_ACTIONS.UPDATE_BUNDLE_FREE_TO_PAID, properties);
}

/**
 * Builds the free-to-paid transition action when a purchase requires moving the
 * bundle off a free plan. The bundle must be transitioned when the product being
 * purchased against is itself on a free plan (dummy/freemium) AND the bundle isn't
 * already paid. Callers must add the returned action to the cart
 * *before* the purchase/plan-change action, as BLL processes the cart in order.
 * @param {Object} params - Action parameters
 * @param {Object} [params.currentProductPlan] - planDetails of the product being purchased against
 * @param {Object} [params.currentPlan] - current plan details; its `bundlePlanType` decides whether the bundle is already paid
 * @param {string} [params.billingCycle] - billing cycle to apply when transitioning to paid
 * @returns {Object|null} The UpdateBundleFreeToPaid cart action, or null when the transition isn't required
 */
export function createFreeToPaidActionIfRequired({
  currentProductPlan,
  currentPlan,
  billingCycle,
} = {}) {
  const isProductOnFreePlan =
    currentProductPlan && (isDummyPlan(currentProductPlan) || isFreemiumPlan(currentProductPlan));

  if (isProductOnFreePlan && !isPaidBundle(currentPlan)) {
    return createUpdateBundleFreeToPaidAction({ properties: { billingCycle } });
  }

  return null;
}

/**
 * Creates a create order action
 * @param {Object} params - Action parameters
 * @param {string} params.orderType - The order type (e.g., "MailSuite", "Site")
 * @param {string} params.planType - The plan type (e.g., "premium", "pro")
 * @param {Object} [params.properties={}] - Additional properties
 * @returns {Object} The cart action
 * @throws {Error} If required parameters are missing
 */
export function createCreateOrderAction({ orderType, planType, properties = {} } = {}) {
  validateRequiredFields({ orderType, planType }, "createCreateOrderAction");

  return createCartAction(BUNDLE_CART_ACTIONS.CREATE_ORDER, {
    orderType,
    planType,
    ...properties,
  });
}

/**
 * Creates a payload for buying additional email accounts
 * @param {Object} params - Payload parameters
 * @param {Object} [params.mail={}] - Mail order details
 * @param {number} params.mail.orderId - The order ID
 * @param {number} params.mail.additionalAccountsToBuy - Number of additional accounts to buy
 * @param {Object} [params.bundle={}] - Bundle metadata
 * @returns {Object} The complete bundle purchase payload
 * @throws {Error} If mail details are invalid
 */
export function createBuyEmailAccountsPayload({ mail = {}, bundle = {} } = {}) {
  const action = createChangeMailOrderNoOfAccountsAction({
    orderId: mail.orderId,
    additionalAccountsToBuy: mail.additionalAccountsToBuy,
  });

  if (!action) {
    throw new Error(
      "`createBuyEmailAccountsPayload` requires a valid orderId and additionalAccountsToBuy"
    );
  }

  return createBundlePurchasePayload({
    actions: [action],
    bundle,
  });
}

/**
 * Creates a payload for provisioning a new plan order
 * @param {Object} params - The provisioning parameters
 * @param {string} params.orderType - The order type (e.g., "MailSuite", "Site")
 * @param {string} params.planType - The plan type (e.g., "premium", "pro")
 * @param {string} params.billingCycle - The billing cycle (e.g., "monthly", "yearly")
 * @param {number} [params.pricingId] - Optional pricing ID
 * @param {number} [params.targetNoOfAccounts] - Optional target number of accounts (for MailSuite)
 * @param {Object} [params.bundle={}] - Bundle metadata
 * @returns {Object} The complete bundle purchase payload
 * @throws {Error} If required parameters are missing
 */
export function createProvisionPlanPayload({
  orderType,
  planType,
  billingCycle,
  pricingId,
  targetNoOfAccounts,
  bundle = {},
} = {}) {
  validateRequiredFields({ orderType, planType, billingCycle }, "createProvisionPlanPayload");

  const properties = {
    newBillingCycle: billingCycle,
  };

  if (pricingId != null) {
    properties.pricingId = pricingId;
  }

  addAccountCountIfValid(properties, orderType, targetNoOfAccounts);

  const action = createCreateOrderAction({
    orderType,
    planType,
    properties,
  });

  return createBundlePurchasePayload({
    actions: [action],
    bundle,
  });
}

/**
 * Creates a payload for upgrading an existing plan
 * @param {Object} params - The upgrade parameters
 * @param {number} params.orderId - The existing order ID
 * @param {string} params.orderType - The order type (e.g., "MailSuite", "Site")
 * @param {string} params.planType - The plan type (e.g., "premium", "pro")
 * @param {string} params.billingCycle - The billing cycle (e.g., "monthly", "yearly")
 * @param {number} [params.additionalAccountsToBuy] - Optional additional accounts to buy (for MailSuite)
 * @param {Object} [params.bundle={}] - Bundle metadata
 * @returns {Object} The complete bundle purchase payload
 * @throws {Error} If required parameters are missing
 */
export function createUpgradePlanPayload({
  orderId,
  orderType,
  planType,
  billingCycle,
  additionalAccountsToBuy,
  currentProductPlan,
  currentPlan,
  bundle = {},
} = {}) {
  validateRequiredFields(
    { orderId, orderType, planType, billingCycle },
    "createUpgradePlanPayload"
  );

  const actions = [];

  const actionPayload = {
    orderId,
    orderType,
    planType,
    additionalAccountsToBuy,
  };

  // the order is important here, as BLL needs to know this is a free-to-paid upgrade before the plan change
  const freeToPaidAction = createFreeToPaidActionIfRequired({
    currentProductPlan,
    currentPlan,
    billingCycle,
  });
  if (freeToPaidAction) {
    actions.push(freeToPaidAction);
  }

  actions.push(
    createChangeOrderPlanAction({
      ...actionPayload,
      properties: {
        newBillingCycle: billingCycle,
      },
    })
  );

  return createBundlePurchasePayload({
    actions,
    bundle,
  });
}

/**
 * Creates a payload for renewing a bundle
 * @param {Object} params - The renewal parameters
 * @param {number} [params.noOfAccountsToRenew] - Optional number of accounts to renew (for MailSuite)
 * @param {boolean} [params.chargeCancelledOrders] - Whether to charge cancelled orders (default: true)
 * @param {boolean} [params.give1MonthExtension] - Whether to give 1 month extension (default: false)
 * @param {Object} [params.bundle={}] - Bundle metadata (currency, promoCodeId, bundleId, etc.)
 * @returns {Object} The complete bundle renewal payload
 * @throws {Error} If required parameters are missing
 */
export function createRenewBundlePayload({
  noOfAccountsToRenew,
  chargeCancelledOrders = false,
  give1MonthExtension = false,
  bundle = {},
} = {}) {
  const { bundleId } = bundle;
  validateRequiredFields({ bundleId }, "createRenewBundlePayload");

  const properties = {
    bundleId,
    chargeCancelledOrders,
  };

  if (noOfAccountsToRenew != null && toFiniteNumber(noOfAccountsToRenew)) {
    properties.noOfAccountsToRenew = noOfAccountsToRenew;
  }

  if (give1MonthExtension) {
    properties.attrs = {
      give1MonthExtension: true,
    };
  }

  const action = createCartAction(BUNDLE_CART_ACTIONS.RENEW_BUNDLE, properties);

  return createBundlePurchasePayload({
    actions: [action],
    bundle,
  });
}

/**
 * Creates a payload for reactivating a cancelled order
 * @param {Object} params - The reactivation parameters
 * @param {number} params.orderId - The order ID to reactivate
 * @param {Object} [params.bundle={}] - Bundle metadata (bundleId, currency, etc.)
 * @returns {Object} The complete bundle purchase payload
 * @throws {Error} If required parameters are missing
 */

export function createReactivateOrderPayload({ orderId, bundle = {} } = {}) {
  validateRequiredFields({ orderId }, "createReactivateOrderPayload");

  const { bundleId } = bundle;
  validateRequiredFields({ bundleId }, "createReactivateOrderPayload");

  const action = createCartAction(BUNDLE_CART_ACTIONS.REACTIVATE_ORDER, {
    orderId,
  });

  return createBundlePurchasePayload({
    actions: [action],
    bundle,
  });
}

/**
 * Creates a payload for purchasing mail order addons
 * @param {Object} params - The addon purchase parameters
 * @param {Array} params.addons - Array of addon purchase objects
 * @param {number} params.addons[].addonId - The addon ID (numeric)
 * @param {Array} params.addons[].attachTo - Array of account IDs to attach addon to (MANDATORY)
 * @param {Object} [params.bundle={}] - Bundle metadata (bundleId, currency, promoCodeId, etc.)
 * @param {string} [params.billingCycle] - Billing cycle, required to transition a free bundle to paid
 * @param {Object} [params.currentProductPlan] - planDetails of the mail product the addon attaches to
 * @param {Object} [params.currentPlan] - Bundle-level current plan details, used to detect a free bundle
 * @returns {Object} The complete bundle purchase payload
 * @throws {Error} If required parameters are missing
 */
export function createAddonPurchasePayload({
  addons = [],
  bundle = {},
  billingCycle,
  currentProductPlan,
  currentPlan,
} = {}) {
  if (!Array.isArray(addons) || !addons.length) {
    throw new Error("`createAddonPurchasePayload` requires at least one addon");
  }

  // Transform addons array to match backend expected format
  const addonProperties = addons.map(addon => {
    const addonId = toFiniteNumber(addon.addonId);
    if (addonId == null) {
      throw new Error("`createAddonPurchasePayload` requires a valid addonId for each addon");
    }

    // attachTo is mandatory
    if (!Array.isArray(addon.attachTo) || addon.attachTo.length === 0) {
      throw new Error(
        "`createAddonPurchasePayload` requires a non-empty attachTo array for each addon"
      );
    }

    const attachTo = addon.attachTo.map(id => toFiniteNumber(id)).filter(Boolean);
    if (attachTo.length === 0) {
      throw new Error(
        "`createAddonPurchasePayload` requires valid numeric account IDs in attachTo array"
      );
    }

    return {
      addonId,
      attachTo,
    };
  });

  const properties = {
    addons: addonProperties,
  };

  const actions = [];

  // order matters: the free-to-paid transition must precede the addon purchase
  const freeToPaidAction = createFreeToPaidActionIfRequired({
    currentProductPlan,
    currentPlan,
    billingCycle,
  });
  if (freeToPaidAction) {
    actions.push(freeToPaidAction);
  }

  actions.push(createCartAction(BUNDLE_CART_ACTIONS.MAIL_ORDER_ADDON_PURCHASE, properties));

  return createBundlePurchasePayload({
    actions,
    bundle,
  });
}
